import { useEffect } from 'react';
import { SplashScreen } from '@syntra/ui/components/loading-screen';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useGetUserAccount } from 'src/domains/user-account/api';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

/**
 * OnboardingGuard ensures user has completed onboarding:
 * - Has an active_user_page with type 'business'
 * - The active_user_page has a company with an active subscription
 * If not complete, redirects to user-after-login page
 * Use this guard on routes that require completed onboarding
 */
export default function OnboardingGuard({ children }: Props) {
  const router = useRouter();
  const { userAccount, userAccountLoading } = useGetUserAccount();

  useEffect(() => {
    // Only redirect if we have data and onboarding is not complete
    if (!userAccountLoading && userAccount) {
      const activeUserPage = userAccount.active_user_page;
      const hasBusinessPage = activeUserPage?.type === 'business';
      const company = activeUserPage?.company;
      const subscription = company?.company_subscription;
      const hasValidSubscription =
        company?.subscription_exempt ||
        subscription?.subscription_status === 'active' ||
        subscription?.subscription_status === 'trialing';

      const isOnboardingComplete = hasBusinessPage && company && hasValidSubscription;

      if (!isOnboardingComplete) {
        router.replace(paths.dashboard.user_after_login.index);
      }
    }
  }, [userAccount, userAccountLoading, router]);

  // Show loading while checking onboarding status
  if (userAccountLoading) {
    return <SplashScreen />;
  }

  // If onboarding is not complete, show nothing (will redirect)
  if (userAccount) {
    const activeUserPage = userAccount.active_user_page;
    const hasBusinessPage = activeUserPage?.type === 'business';
    const company = activeUserPage?.company;
    const subscription = company?.company_subscription;
    const hasValidSubscription =
      company?.subscription_exempt ||
      subscription?.subscription_status === 'active' ||
      subscription?.subscription_status === 'trialing';

    const isOnboardingComplete = hasBusinessPage && company && hasValidSubscription;

    if (!isOnboardingComplete) {
      return null;
    }
  }

  // Onboarding complete, render children
  return <>{children}</>;
}
